import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API = (process.env.REACT_APP_BASE_API || "https://jeetayu.api.aadviktech.com/api").replace(/\/+$/, "");

const isLoggedIn = () => !!localStorage.getItem("token");
const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// guest wishlist lives in localStorage (same idea as your cart)
const readGuest = () => {
  try {
    return JSON.parse(localStorage.getItem("wishlist")) || [];
  } catch {
    return [];
  }
};
const saveGuest = (items) => localStorage.setItem("wishlist", JSON.stringify(items));

// DB gives "abc.png" -> full URL (same logic as Home / Shop)
const toImageUrl = (path) => {
  const src = typeof path === "string" ? path : path?.url;
  if (!src) return "";
  if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) return src;
  const clean = String(src).replace(/^\/+/, "");
  return clean.includes("/") ? `${API}/${clean}` : `${API}/uploads/${clean}`;
};

const fetchServerList = async () => {
  const { data } = await axios.get(`${API}/wishlist`, authHeader());
  return data.items.map((i) => ({
    id: i.id,
    name: i.name,
    price: Number(i.price),
    category: i.category,
    image: toImageUrl(i.img),
  }));
};

/* Call after login (and on page load when logged in):
   moves guest items into MySQL, clears local copy, loads the DB list. */
export const syncWishlist = createAsyncThunk(
  "wishlist/sync",
  async (_, { rejectWithValue }) => {
    try {
      const guest = readGuest();
      if (guest.length) {
        await axios.post(
          `${API}/wishlist/sync`,
          { productIds: guest.map((i) => i.id) },
          authHeader()
        );
      }
      localStorage.removeItem("wishlist");
      return await fetchServerList();
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || "Failed");
    }
  }
);

export const fetchWishlist = createAsyncThunk(
  "wishlist/fetch",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchServerList();
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || "Failed");
    }
  }
);

/* State is already updated optimistically in the "pending" case below,
   so for guests we just save the new state to localStorage. */
export const toggleWishlist = createAsyncThunk(
  "wishlist/toggle",
  async (item, { getState, rejectWithValue }) => {
    if (!isLoggedIn()) {
      saveGuest(getState().wishlist.items);
      return item;
    }
    try {
      await axios.post(`${API}/wishlist/toggle`, { productId: item.id }, authHeader());
      return item;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || "Failed");
    }
  }
);

export const removeFromWishlist = createAsyncThunk(
  "wishlist/remove",
  async (item, { getState, rejectWithValue }) => {
    if (!isLoggedIn()) {
      saveGuest(getState().wishlist.items);
      return item;
    }
    try {
      await axios.delete(`${API}/wishlist/${item.id}`, authHeader());
      return item;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || "Failed");
    }
  }
);

export const clearWishlist = createAsyncThunk(
  "wishlist/clear",
  async (_, { rejectWithValue }) => {
    if (!isLoggedIn()) {
      localStorage.removeItem("wishlist");
      return;
    }
    try {
      await axios.delete(`${API}/wishlist`, authHeader());
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || "Failed");
    }
  }
);

const toggleLocal = (state, item) => {
  const exists = state.items.some((i) => i.id === item.id);
  state.items = exists
    ? state.items.filter((i) => i.id !== item.id)
    : [...state.items, item];
};

const wishlistSlice = createSlice({
  name: "wishlist",
  initialState: { items: readGuest(), loading: false, error: null },
  reducers: {
    // call on logout
    resetWishlist: (state) => {
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(syncWishlist.fulfilled, (s, a) => { s.items = a.payload; })
      .addCase(fetchWishlist.pending, (s) => { s.loading = true; })
      .addCase(fetchWishlist.fulfilled, (s, a) => { s.loading = false; s.items = a.payload; })
      .addCase(fetchWishlist.rejected, (s, a) => { s.loading = false; s.error = a.payload; })

      // optimistic toggle: flip now, flip back if the request fails
      .addCase(toggleWishlist.pending, (s, a) => toggleLocal(s, a.meta.arg))
      .addCase(toggleWishlist.rejected, (s, a) => toggleLocal(s, a.meta.arg))

      // optimistic remove
      .addCase(removeFromWishlist.pending, (s, a) => {
        s.items = s.items.filter((i) => i.id !== a.meta.arg.id);
      })
      .addCase(removeFromWishlist.rejected, (s, a) => {
        if (!s.items.some((i) => i.id === a.meta.arg.id)) s.items.push(a.meta.arg);
      })

      .addCase(clearWishlist.pending, (s) => { s.items = []; });
  },
});

export const { resetWishlist } = wishlistSlice.actions;
export default wishlistSlice.reducer;
